<div class="dashboard-toolbar">
    <span class="dashboard-range-label">
        Metrics for {{ gridStart() | date: 'mediumDate' }} – {{ gridRangeEnd() | date: 'mediumDate' }}
        ({{ horizonWeekdays }} weekdays)
    </span>
    <button type="button" class="btn btn-outline-secondary btn-sm dashboard-refresh" (click)="refresh()" [disabled]="isLoading()">
        {{ isLoading() ? 'Loading…' : 'Refresh' }}
    </button>
</div>

@if (isLoading() && !context()) {
    <p class="text-muted dashboard-status">Loading dashboard…</p>
} @else if (loadError()) {
    <p class="text-danger dashboard-status">{{ loadError() }}</p>
} @else {
    <div class="dashboard-kpi-grid">
        <div class="dashboard-kpi-card">
            <span class="dashboard-kpi-value">{{ kpis().foremanCount }}</span>
            <span class="dashboard-kpi-label">Crews (foremen)</span>
        </div>
        <div class="dashboard-kpi-card">
            <span class="dashboard-kpi-value">{{ kpis().crewMemberCount }}</span>
            <span class="dashboard-kpi-label">Crew members</span>
        </div>
        <div class="dashboard-kpi-card">
            <span class="dashboard-kpi-value">{{ kpis().crewsWorkingToday }}</span>
            <span class="dashboard-kpi-label">Crews working today</span>
            <span class="dashboard-kpi-sub">{{ kpis().peopleWorkingToday }} people deployed</span>
        </div>
        <div class="dashboard-kpi-card">
            <span class="dashboard-kpi-value">{{ kpis().peopleOffToday }}</span>
            <span class="dashboard-kpi-label">Off today (PTO)</span>
        </div>
        <div class="dashboard-kpi-card">
            <span class="dashboard-kpi-value">{{ kpis().activeJobCount }}</span>
            <span class="dashboard-kpi-label">Active jobs</span>
        </div>
        <div class="dashboard-kpi-card">
            <span class="dashboard-kpi-value">{{ kpis().coveragePercent }}%</span>
            <span class="dashboard-kpi-label">Schedule coverage</span>
        </div>
        <div class="dashboard-kpi-card" [class.dashboard-kpi-warn]="kpis().crewsRunningOutCount > 0">
            <span class="dashboard-kpi-value">{{ kpis().crewsRunningOutCount }}</span>
            <span class="dashboard-kpi-label">Crews running out of work</span>
        </div>
        <div class="dashboard-kpi-card" [class.dashboard-kpi-danger]="kpis().jobsOverEstimateCount > 0">
            <span class="dashboard-kpi-value">{{ kpis().jobsOverEstimateCount }}</span>
            <span class="dashboard-kpi-label">Jobs over estimate</span>
        </div>
        <div class="dashboard-kpi-card">
            <span class="dashboard-kpi-value">{{ kpis().activeLendCount }}</span>
            <span class="dashboard-kpi-label">Active crew lends</span>
        </div>
    </div>

    <div class="dashboard-panels">
        <section class="dashboard-panel">
            <h2 class="dashboard-panel-title">Crews running out of work</h2>
            @if (!runningOutRows().length) {
                <p class="text-muted dashboard-empty">Every crew has work scheduled through the horizon.</p>
            } @else {
                <table class="table table-sm dashboard-table">
                    <thead>
                        <tr>
                            <th>Foreman</th>
                            <th class="dashboard-num">Scheduled days</th>
                            <th>Last day with work</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (row of runningOutRows(); track row.hrRef) {
                            <tr [class.dashboard-row-danger]="!row.hasWork">
                                <td>{{ row.name }}</td>
                                <td class="dashboard-num">{{ row.scheduledDays }} / {{ horizonWeekdays }}</td>
                                <td>{{ row.lastWorkLabel }}</td>
                            </tr>
                        }
                    </tbody>
                </table>
            }
        </section>

        <section class="dashboard-panel">
            <h2 class="dashboard-panel-title">Jobs over estimate</h2>
            @if (!jobsOverEstimateRows().length) {
                <p class="text-muted dashboard-empty">No scheduled job/phase is projected beyond its estimate.</p>
            } @else {
                <table class="table table-sm dashboard-table">
                    <thead>
                        <tr>
                            <th>Job / Phase</th>
                            <th class="dashboard-num">Est</th>
                            <th class="dashboard-num">Proj</th>
                            <th class="dashboard-num">Over</th>
                            <th class="dashboard-num">Crews</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (row of jobsOverEstimateRows(); track row.vpJobId + '|' + row.phase) {
                            <tr>
                                <td>
                                    <span class="dashboard-strong">{{ row.vpJobId }} / {{ row.phase }}</span>
                                    @if (row.jobName) {
                                        <span class="dashboard-muted-line">{{ row.jobName }}</span>
                                    }
                                </td>
                                <td class="dashboard-num">{{ row.estHours | number: '1.0-0' }}</td>
                                <td class="dashboard-num">{{ row.projHours | number: '1.0-0' }}</td>
                                <td class="dashboard-num dashboard-over">{{ row.overHours | number: '1.0-0' }}</td>
                                <td class="dashboard-num">{{ row.crewCount }}</td>
                            </tr>
                        }
                    </tbody>
                </table>
            }
        </section>

        <section class="dashboard-panel">
            <h2 class="dashboard-panel-title">Upcoming time off</h2>
            @if (!upcomingPtoRows().length) {
                <p class="text-muted dashboard-empty">No crew time off in this window.</p>
            } @else {
                <table class="table table-sm dashboard-table">
                    <thead>
                        <tr>
                            <th>Member</th>
                            <th>Crew</th>
                            <th>Dates</th>
                            <th>Type</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (row of upcomingPtoRows(); track row.key) {
                            <tr>
                                <td>{{ row.memberName }}</td>
                                <td>{{ row.foremanName }}</td>
                                <td>{{ row.startDate | date: 'MMM d' }} – {{ row.endDate | date: 'MMM d' }}</td>
                                <td>{{ row.leaveType }}</td>
                                <td>
                                    <span
                                        class="dashboard-status-chip"
                                        [class.dashboard-status-approved]="row.status === 'Approved'"
                                        [class.dashboard-status-pending]="row.status === 'Pending'"
                                    >{{ row.status }}</span>
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            }
        </section>

        <section class="dashboard-panel">
            <h2 class="dashboard-panel-title">Per-crew coverage</h2>
            @if (!crewCoverageRows().length) {
                <p class="text-muted dashboard-empty">No crews found.</p>
            } @else {
                <table class="table table-sm dashboard-table">
                    <thead>
                        <tr>
                            <th>Foreman</th>
                            <th class="dashboard-num">Scheduled days</th>
                            <th class="dashboard-num">Hours</th>
                            <th class="dashboard-num">Coverage</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (row of crewCoverageRows(); track row.hrRef) {
                            <tr>
                                <td>{{ row.name }}</td>
                                <td class="dashboard-num">{{ row.scheduledDays }} / {{ horizonWeekdays }}</td>
                                <td class="dashboard-num">{{ row.totalHours | number: '1.0-0' }}</td>
                                <td class="dashboard-num">
                                    <div class="dashboard-coverage-bar" [title]="row.coveragePercent + '%'">
                                        <div class="dashboard-coverage-fill" [style.width.%]="row.coveragePercent"></div>
                                        <span class="dashboard-coverage-text">{{ row.coveragePercent }}%</span>
                                    </div>
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            }
        </section>
    </div>
}
